import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Textarea,
  Alert,
} from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getCustomerDestinationTypeOptions,
  getCustomerDestinationPlaceholder,
} from '../../utils/customerDestinationTypes';

const textSecondary = '#64748b';
const textMuted = '#94a3b8';
const textPrimary = '#1e293b';

const DESTINATION_CARD_STYLES = {
  home: {
    bg: '#f8fafc',
    selectedBg: '#eff6ff',
    border: '#e2e8f0',
    selectedBorder: '#3b82f6',
    iconBg: '#dbeafe',
    iconColor: '#2563eb',
  },
  store: {
    bg: '#f8fafc',
    selectedBg: '#ecfdf5',
    border: '#e2e8f0',
    selectedBorder: '#10b981',
    iconBg: '#d1fae5',
    iconColor: '#059669',
  },
  branch: {
    bg: '#f8fafc',
    selectedBg: '#fff7ed',
    border: '#e2e8f0',
    selectedBorder: '#f97316',
    iconBg: '#ffedd5',
    iconColor: '#ea580c',
  },
};

function DestinationTypeCard({ option, selected, onSelect, disabled }) {
  const styles = DESTINATION_CARD_STYLES[option.value] || DESTINATION_CARD_STYLES.home;
  const Icon = option.icon;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => !disabled && onSelect(option.value)}
      disabled={disabled}
      aria-pressed={selected}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        p: 1.5,
        textAlign: 'start',
        borderRadius: '12px',
        border: '2px solid',
        borderColor: selected ? styles.selectedBorder : styles.border,
        bgcolor: selected ? styles.selectedBg : styles.bg,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
        boxShadow: selected ? `0 0 0 1px ${styles.selectedBorder}22` : 'none',
        '&:hover': disabled
          ? undefined
          : {
              borderColor: styles.selectedBorder,
              bgcolor: styles.selectedBg,
            },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          bgcolor: styles.iconBg,
          color: styles.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 700, mb: 0.25 }}>
          {option.title}
        </Typography>
        <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.45 }}>
          {option.description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function CustomerDeliveryDestinationForm({
  destinationType,
  onDestinationTypeChange,
  destinationLocation,
  onDestinationLocationChange,
  siteName,
  disabled = false,
  showComparisonHint = true,
}) {
  const { t } = useTranslation('ui');
  const options = useMemo(() => getCustomerDestinationTypeOptions(t, siteName), [t, siteName]);
  const selectedOption = options.find((opt) => opt.value === destinationType) || options[0];
  const placeholder = getCustomerDestinationPlaceholder(destinationType, t, siteName);

  return (
    <Box>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1 }}>{t('customer.destinationTypeForm')}</FormLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map((option) => (
            <DestinationTypeCard
              key={option.value}
              option={option}
              selected={destinationType === option.value}
              onSelect={onDestinationTypeChange}
              disabled={disabled}
            />
          ))}
        </Box>
      </FormControl>

      {showComparisonHint && (
        <Alert
          variant="soft"
          color="neutral"
          size="sm"
          startDecorator={<InfoOutlinedIcon />}
          sx={{
            mb: 2,
            borderRadius: '10px',
            alignItems: 'flex-start',
            '& .JoyAlert-message': { fontSize: '0.8125rem', lineHeight: 1.5 },
          }}
        >
          {t('customer.deliveryDestination.storeVsBranchHint', { site: siteName })}
        </Alert>
      )}

      {selectedOption && (
        <Alert
          variant="soft"
          color="primary"
          size="sm"
          sx={{
            mb: 2,
            borderRadius: '10px',
            alignItems: 'flex-start',
            '& .JoyAlert-message': { fontSize: '0.8125rem', lineHeight: 1.5 },
          }}
        >
          {selectedOption.hint}
        </Alert>
      )}

      <FormControl>
        <FormLabel>{t('customer.destinationLocationFormLabel')}</FormLabel>
        <Textarea
          placeholder={placeholder}
          value={destinationLocation}
          onChange={(e) => onDestinationLocationChange(e.target.value.slice(0, 300))}
          minRows={2}
          maxRows={4}
          disabled={disabled}
          sx={{ borderRadius: '10px' }}
        />
        <Typography level="body3" sx={{ color: textMuted, mt: 0.5 }}>
          {destinationLocation.length}/300
        </Typography>
      </FormControl>
    </Box>
  );
}
